<template>
  <div v-if="show" class="bg">
    <button type="button" class="preview-close" @click="close" :title="$tt('关闭预览')">
      <i class="el-icon-close" />
    </button>

    <div class="pad-shell">
      <div class="pad-bezel">
        <div class="pad-sensor">
          <span class="pad-cam" />
          <span class="pad-mic" />
        </div>

        <div class="pad-screen" ref="padScreen">
          <button
            type="button"
            class="drawer-toggle"
            :class="{ open: drawerOpen }"
            :title="drawerOpen ? '收起页面列表' : '打开页面列表'"
            @click="drawerOpen = !drawerOpen"
          >
            <i :class="drawerOpen ? 'el-icon-arrow-left' : 'el-icon-arrow-right'" />
          </button>

          <aside class="page-drawer" :class="{ open: drawerOpen }" ref="pageDrawer">
            <div class="drawer-head">页面</div>
            <button
              v-show="drawerCanScrollUp"
              type="button"
              class="drawer-more up"
              :title="$tt('查看上方页面')"
              @click="scrollDrawerPages(-1)"
            >
              <i class="el-icon-caret-top" />
            </button>
            <div class="drawer-list" ref="drawerList" @scroll="updateDrawerOverflow">
              <button
                v-for="item in previewPageList"
                :key="item.value"
                type="button"
                class="page-chip"
                :class="{ active: currentPage === item.value, child: item.level > 0 }"
                :style="{ paddingLeft: 12 + item.level * 14 + 'px' }"
                @click="onSelectPage(item.value)"
              >
                {{ item.label }}
              </button>
            </div>
            <button
              v-show="drawerCanScrollDown"
              type="button"
              class="drawer-more down"
              :title="$tt('查看下方页面')"
              @click="scrollDrawerPages(1)"
            >
              <i class="el-icon-caret-bottom" />
            </button>
          </aside>

          <div class="canvas-container">
            <div
              class="canvas preview-canvas"
              ref="canvas"
              :style="Object.assign({}, {
                width: getCanvasStyleData.width + 'px',
                height: getCanvasStyleData.height + 'px',
                backgroundSize: `100% 100%`,
                backgroundRepeat: 'no-repeat',
                transformOrigin: '0 0'
              }, getCanvasStyleData.backPicSrc ? {
                backgroundImage: `url(${getCanvasStyleData.backPicSrc})`
              } : {
                backgroundColor: getCanvasStyleData.backgroundColor || ''
              })"
            >
              <ComponentWrapper
                v-for="item in useableComponentsData"
                :key="item.id"
                :config="item"
                :allBtnPressMap="allBtnPressMap"
                :currentComps="currentComponent"
                @changeCurComp="changeCurComp"
                @changePage="changePage"
                @changeBtnPress="changeBtnPress"
              />
            </div>
          </div>
        </div>

        <div class="pad-home">
          <span class="pad-home-bar" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getStyle } from "@/utils/style";
import { getPicSrc, isEmpty } from "@/utils/utils";
import { mapState } from "vuex";
import ComponentWrapper from "./ComponentWrapper";
import eventBus from "@/utils/eventBus";
import { parseCanvasAllData } from "@/utils/canvasPageLoader";

export default {
  components: { ComponentWrapper },
  model: {
    prop: "show",
    event: "change",
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: "",
      currentComponent: JSON.stringify([]),
      allBtnPressMap: {},
      drawerOpen: true,
      drawerCanScrollDown: false,
      drawerCanScrollUp: false,
    };
  },
  watch: {
    show: function (val, oldVal) {
      if (val && val !== oldVal) {
        this.drawerOpen = true;
        this.ensurePreviewPagesReady().then(() => {
          this.currentPage = this.selectedCurrentPage + "";
          this.currentComponent = JSON.stringify(this.componentData);
          this.$nextTick(() => {
            this.setEditorReSize();
            this.initCompLinkEvents();
            this.bindPadWheel();
            this.updateDrawerOverflow();
            this.$nextTick(() => this.setEditorReSize());
          });
        });
      } else if (!val) {
        this.unbindPadWheel();
      }
    },
    drawerOpen() {
      this.$nextTick(() => this.updateDrawerOverflow());
    },
    previewPageList() {
      this.$nextTick(() => this.updateDrawerOverflow());
    },
  },
  computed: {
    ...mapState([
      "componentData",
      "canvasStyleData",
      "projectStyleData",
      "pageOptions",
      "selectedCurrentPage",
      "canvasAllData",
      "canvasLazyLoad",
    ]),
    getCanvasStyleData() {
      const first = JSON.parse(this.currentComponent)[0];
      const style = (first && first.style) || {};
      const { backPicSrc, backgroundColor } = style;
      const isChild = first && first.isChildPage;
      const width =
        isChild && style.pageWidth
          ? style.pageWidth
          : this.projectStyleData.width;
      const height =
        isChild && style.pageHeight
          ? style.pageHeight
          : this.projectStyleData.height;
      return {
        width,
        height,
        backPicSrc: getPicSrc(backPicSrc),
        backgroundColor: backgroundColor,
      };
    },
    useableComponentsData() {
      return JSON.parse(this.currentComponent).filter((c) => c.id != "Back");
    },
    previewPageList() {
      const list = [];
      const walk = (arr, level) => {
        (arr || []).forEach((p) => {
          list.push({
            label: p.label,
            value: String(p.value),
            level: level || 0,
          });
          if (p.children && p.children.length) {
            walk(p.children, (level || 0) + 1);
          }
        });
      };
      walk(this.pageOptions, 0);
      return list;
    },
  },
  mounted() {
    window.addEventListener("resize", this.setEditorReSize);
    if (this.show) {
      this.bindPadWheel();
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setEditorReSize);
    this.unbindPadWheel();
  },
  methods: {
    bindPadWheel() {
      this.unbindPadWheel();
      const el = this.$refs.padScreen;
      if (!el) {
        return;
      }
      el.addEventListener("wheel", this.onPreviewWheel, { passive: false });
    },
    unbindPadWheel() {
      const el = this.$refs.padScreen;
      if (!el) {
        return;
      }
      el.removeEventListener("wheel", this.onPreviewWheel);
    },
    onPreviewWheel(e) {
      if (!this.drawerOpen) {
        return;
      }
      const drawer = this.$refs.pageDrawer;
      const list = this.$refs.drawerList;
      if (!drawer || !list) {
        return;
      }
      const rect = drawer.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        return;
      }
      if (list.scrollHeight <= list.clientHeight) {
        return;
      }
      e.preventDefault();
      list.scrollTop += e.deltaY;
      this.updateDrawerOverflow();
    },
    updateDrawerOverflow() {
      const el = this.$refs.drawerList;
      if (!el || !this.drawerOpen) {
        this.drawerCanScrollDown = false;
        this.drawerCanScrollUp = false;
        return;
      }
      this.drawerCanScrollUp = el.scrollTop > 4;
      this.drawerCanScrollDown =
        el.scrollHeight - el.scrollTop - el.clientHeight > 4;
    },
    scrollDrawerPages(dir) {
      const el = this.$refs.drawerList;
      if (!el) {
        return;
      }
      const step = 56;
      el.scrollTop += dir * step;
      this.updateDrawerOverflow();
    },
    onSelectPage(val) {
      this.currentPage = val + "";
      this.pageChange(val);
    },
    previewFindCompById(components, compId) {
      if (!components || !compId) {
        return null;
      }
      for (let i = 0; i < components.length; i++) {
        const c = components[i];
        if (String(c.id) === String(compId)) {
          return c;
        }
        if (c.propValue && Array.isArray(c.propValue)) {
          const sub = this.previewFindCompById(c.propValue, compId);
          if (sub) {
            return sub;
          }
        }
      }
      return null;
    },
    previewCompDisplayName(compId) {
      let c = this.previewFindCompById(this.useableComponentsData, compId);
      if (!c) {
        for (let i = 0; i < this.useableComponentsData.length; i++) {
          const item = this.useableComponentsData[i];
          if (item.component === "ChildPage") {
            const comps = this.getChildPageComps(item);
            if (comps && comps.length) {
              c = this.previewFindCompById(comps, compId);
              if (c) {
                break;
              }
            }
          }
        }
      }
      if (!c) {
        return String(compId);
      }
      if (c.component === "v-button") {
        return c.propValue || (c.style && c.style.alias) || c.id;
      }
      return c.label || c.component || c.id;
    },
    initCompLinkEvents() {
      var linkEventsMap = {};
      var _this = this;
      this.useableComponentsData.forEach((item) => {
        linkEventsMap = _this.setLinksEvents(item, linkEventsMap);
        if (item.component === "ChildPage") {
          var comps = _this.getChildPageComps(item);
          if (comps && comps.length !== 0) {
            comps.forEach((childItem) => {
              linkEventsMap = _this.setLinksEvents(childItem, linkEventsMap);
            });
          }
        }
      });
      var previewCanvas = document.querySelector(".preview-canvas");
      var clickMap = {};
      this.$nextTick(() => {
        for (let key in linkEventsMap) {
          let events = linkEventsMap[key];
          let elemDiv = previewCanvas && previewCanvas.querySelector(`[data-id="${key}"]`);
          if (elemDiv) {
            let elem = elemDiv.children[0];
            elem.onclick = () => {
              const sourceName = this.previewCompDisplayName(key);
              const applyAttrs = (list, title) => {
                if (!list || !list.length) {
                  return;
                }
                console.log(
                  `[预览·组件联动·${title}] 触发源组件「${sourceName}」（id：${key}）。按「组件联动」配置执行样式变更：`
                );
                list.forEach((attr) => this.applyPreviewLinkAttr(previewCanvas, attr));
              };
              if (events.clickAgain) {
                if (!clickMap[key]) {
                  applyAttrs(events.click, "首次点击");
                } else {
                  applyAttrs(events.clickAgain, "再次点击");
                }
                clickMap[key] = !clickMap[key];
              } else {
                applyAttrs(events.click, "点击");
              }
            };
          }
        }
      });
    },
    applyPreviewLinkAttr(previewCanvas, attr) {
      if (!previewCanvas || !attr) {
        return;
      }
      var changeElemDiv = previewCanvas.querySelector(
        `[data-id="${attr.changeComp}"]`
      );
      if (!changeElemDiv) {
        console.warn(
          `[预览·组件联动] 未找到目标组件节点 id：${attr.changeComp}，已跳过该条样式联动。`
        );
        return;
      }
      const layoutKeys = ["left", "top", "width", "height"];
      var changeElem = layoutKeys.indexOf(attr.key) !== -1
        ? changeElemDiv
        : (changeElemDiv.children[0] || changeElemDiv);
      const targetName = this.previewCompDisplayName(attr.changeComp);
      const val = attr.value + (attr.key !== "opacity" ? "px" : "");
      const ms = Number(attr.time) || 0;
      console.log(
        `  → 目标组件「${targetName}」（id：${attr.changeComp}）：将 CSS 属性「${attr.key}」设置为「${val}」，过渡时间 ${ms}ms。`
      );
      changeElem.style.transition = `${attr.key} ${ms}ms`;
      changeElem.style[attr.key] = val;
    },
    setLinksEvents(item, linkEventsMap) {
      if (
        item.componentLinks &&
        item.componentLinks.links &&
        item.componentLinks.links.length !== 0
      ) {
        item.componentLinks.links.forEach((linkItem) => {
          if (
            linkItem.comp &&
            linkItem.listenEvent &&
            linkItem.changeAttrs &&
            linkItem.changeAttrs.length !== 0
          ) {
            linkItem.changeAttrs.forEach((attr) => {
              if (attr.key && !isEmpty(attr.value)) {
                if (!linkEventsMap[linkItem.comp]) {
                  linkEventsMap[linkItem.comp] = {};
                }
                if (!linkEventsMap[linkItem.comp][linkItem.listenEvent]) {
                  linkEventsMap[linkItem.comp][linkItem.listenEvent] = [];
                }
                linkEventsMap[linkItem.comp][linkItem.listenEvent].push({
                  ...attr,
                  changeComp: item.id,
                  time: item.componentLinks.transitionTime,
                });
              }
            });
          }
        });
      }
      return linkEventsMap;
    },
    ensurePreviewPagesReady() {
      return new Promise((resolve) => {
        if (!this.canvasLazyLoad) {
          resolve();
          return;
        }
        const pageIds = [];
        (this.pageOptions || []).forEach((p) => {
          pageIds.push(p.value);
          if (p.children && p.children.length) {
            p.children.forEach((c) => pageIds.push(c.value));
          }
        });
        eventBus.$emit("ensureCanvasPages", {
          pageIds,
          done: resolve,
        });
      });
    },
    getChildPageComps(element) {
      var childPageComps = [];
      if (element.style.bindPage) {
        var bindPageKey = element.style.bindPage.value;
        const data = parseCanvasAllData(this.canvasAllData);
        if (data[bindPageKey]) {
          var backData = data[bindPageKey].find((a) => a.id === "Back");
          if (
            data[bindPageKey].length > 1 ||
            backData.style.backgroundColor ||
            backData.style.backPicSrc
          ) {
            childPageComps = data[bindPageKey];
          }
        }
      }
      return childPageComps;
    },
    setEditorReSize() {
      const canvas = this.$refs.canvas;
      if (!canvas) {
        return;
      }
      const parentElem = canvas.parentElement;
      if (!parentElem) {
        return;
      }
      const parentW = parentElem.clientWidth;
      const parentH = parentElem.clientHeight;
      const currentActualW = canvas.offsetWidth;
      const currentActualH = canvas.offsetHeight;
      if (!currentActualW || !currentActualH || !parentW || !parentH) {
        return;
      }
      const scale = Math.min(parentW / currentActualW, parentH / currentActualH);
      const left = (parentW - currentActualW * scale) / 2;
      const top = (parentH - currentActualH * scale) / 2;
      canvas.style.transformOrigin = "0 0";
      canvas.style.top = "0px";
      canvas.style.left = "0px";
      canvas.style.transform = `translate(${left}px, ${top}px) scale(${scale})`;
      canvas.setAttribute("data-scale", scale);
    },
    getStyle,
    changeCurComp(datas) {
      this.currentComponent = JSON.stringify(datas);
    },
    changePage(key) {
      this.currentPage = key + "";
      this.pageChange(key);
    },
    changeBtnPress(arr) {
      arr.map((item) => {
        this.$set(this.allBtnPressMap, item.id, item.isPress);
      });
    },
    pageChange(val) {
      const apply = () => {
        const canvasData = parseCanvasAllData(this.canvasAllData);
        this.currentComponent = JSON.stringify(canvasData[val] || []);
        this.$nextTick(() => {
          this.initCompLinkEvents();
          this.updateDrawerOverflow();
          this.scrollActivePageChip();
          this.setEditorReSize();
        });
      };
      if (!this.canvasLazyLoad) {
        apply();
        return;
      }
      eventBus.$emit("ensureCanvasPages", {
        pageIds: [val],
        done: apply,
      });
    },
    scrollActivePageChip() {
      const list = this.$refs.drawerList;
      if (!list) {
        return;
      }
      const active = list.querySelector(".page-chip.active");
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: "nearest" });
      }
      this.updateDrawerOverflow();
    },
    close() {
      this.allBtnPressMap = {};
      this.unbindPadWheel();
      this.$emit("change", false);
    },
  },
};
</script>

<style lang="scss" scoped>
.bg {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  z-index: 1900;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 56px 32px;
  background:
    radial-gradient(ellipse at 50% 18%, rgba(90, 100, 120, 0.35), transparent 55%),
    #0b0d12;
}

.preview-close {
  position: absolute;
  top: 16px;
  right: 18px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
}

.pad-shell {
  width: 100%;
  height: 100%;
  max-width: 1280px;
  filter: drop-shadow(0 28px 64px rgba(0, 0, 0, 0.55));
}

.pad-bezel {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 18px 16px 14px;
  border-radius: 36px;
  background: linear-gradient(160deg, #3a3d44 0%, #1c1e24 42%, #121318 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -2px 6px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
}

.pad-sensor {
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
  .pad-cam {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #4a6288, #0a0c12 70%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  }
  .pad-mic {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #0a0c12;
    opacity: 0.7;
  }
}

.pad-screen {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #111;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.pad-home {
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  .pad-home-bar {
    width: 92px;
    height: 5px;
    border-radius: 99px;
    background: rgba(255, 255, 255, 0.28);
  }
}

.drawer-toggle {
  position: absolute;
  left: 0;
  top: 50%;
  z-index: 5;
  width: 22px;
  height: 64px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0 10px 10px 0;
  background: rgba(28, 32, 42, 0.55);
  color: #fff;
  cursor: pointer;
  transform: translateY(-50%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: auto;
  &.open {
    left: 168px;
  }
  i {
    font-size: 14px;
  }
}

.page-drawer {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 168px;
  z-index: 4;
  pointer-events: none;
  transform: translateX(-100%);
  transition: transform 0.22s ease;
  background: rgba(22, 26, 36, 0.38);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.16);
  box-sizing: border-box;
  padding: 12px 10px 16px;
  display: flex;
  flex-direction: column;
  &.open {
    transform: translateX(0);
  }
}

.drawer-head {
  pointer-events: none;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  letter-spacing: 1px;
  margin-bottom: 10px;
  padding-left: 4px;
}

.drawer-more {
  pointer-events: auto;
  flex-shrink: 0;
  width: 100%;
  height: 22px;
  margin: 0 0 6px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: drawer-caret 1.1s ease-in-out infinite;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &.down {
    margin: 6px 0 0;
  }
  &.up {
    animation-name: drawer-caret-up;
  }
  i {
    font-size: 16px;
  }
}

@keyframes drawer-caret {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.75;
  }
  50% {
    transform: translateY(3px);
    opacity: 1;
  }
}

@keyframes drawer-caret-up {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.75;
  }
  50% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

.drawer-list {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 8px;
  pointer-events: none;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

.page-chip {
  pointer-events: auto;
  flex: 0 0 auto;
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 13px;
  line-height: 1.3;
  padding: 8px 10px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
  &.active {
    background: rgba(64, 158, 255, 0.55);
    border-color: rgba(64, 158, 255, 0.85);
  }
  &.child {
    font-size: 12px;
    opacity: 0.95;
  }
}

.canvas-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
  position: relative;
  .canvas {
    background: #fff;
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>
